import React, { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { authState } from '../store/atoms';
import { useAuth } from '../hooks/useAuth';

interface TeacherRouteProps {
  children: React.ReactNode;
}

const TeacherRoute: React.FC<TeacherRouteProps> = ({ children }) => {
  const auth = useAtomValue(authState);
  const { validateToken } = useAuth();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const hasValidated = useRef(false);

  useEffect(() => {
    const validateTokenAsync = async () => {
      // 이미 검증되었으면 중복 실행 방지
      if (hasValidated.current) {
        return;
      }

      try {
        // 로그인하지 않은 경우
        if (!auth.isAuthenticated) {
          setIsValid(false);
          setIsValidating(false);
          hasValidated.current = true;
          return;
        }

        // 선생님이 아닌 경우
        if (auth.userType !== 'teacher') {
          setIsValid(false);
          setIsValidating(false);
          hasValidated.current = true;
          return;
        }

        // 서버 토큰 검증 (최초 1회만, 캐싱 적용)
        if (!hasValidated.current) {
          const isValid = await validateToken();
          setIsValid(isValid);
          hasValidated.current = true;
        }
      } catch (error) {
        setIsValid(false);
        hasValidated.current = true;
      } finally {
        setIsValidating(false);
      }
    };

    validateTokenAsync();
  }, [auth.isAuthenticated, auth.userType, validateToken]);

  // 검증 중일 때는 로딩 표시
  if (isValidating) {
    return (
      <div className="page-loading">
        <div className="loading-spinner"></div>
        <p>권한 확인 중...</p>
      </div>
    );
  }

  // 검증 실패 시 리다이렉트
  if (!isValid) {
    if (!auth.isAuthenticated) {
      return <Navigate to="/login" replace />;
    } else if (auth.userType === 'user') {
      return <Navigate to="/user/dashboard" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }

  return <>{children}</>;
};

export default TeacherRoute; 